// ===========================================
// Standard Event Ticket Tiers (frontend mirror)
// ===========================================
// Byte-identical mirror of the backend source of truth at
// apps/backend/src/constants/eventTicketTiers.constants.ts. A parity test
// asserts the STANDARD_TICKET_TIERS arrays match — edit BOTH together.
//
// The frontend resolves labels client-side ONLY for editor/display purposes;
// the server is always authoritative for the label persisted on a tier row.
//
// `key`      — stable identifier; never change once shipped. Logic branches on
//              the key, not the (mutable) label.
// `label`    — display text. The token {SCHOOL} is substituted with the
//              school's own name, e.g. "Acme Music Students".
// `sortOrder`— canonical display order in the editor and on event pages.
// `schoolNamed` — true only for the tier whose label embeds the school name.

export interface StandardTicketTier {
  key: string;
  label: string;
  sortOrder: number;
  schoolNamed?: boolean;
}

export const STANDARD_TICKET_TIERS: readonly StandardTicketTier[] = [
  { key: 'SCHOOL_STUDENTS', label: '{SCHOOL} Students', sortOrder: 0, schoolNamed: true },
  { key: 'INFANT',          label: 'Infant (under 2)',  sortOrder: 1 },
  { key: 'PRESCHOOL',       label: 'Preschool (3 - 5)', sortOrder: 2 },
  { key: 'CHILD',           label: 'Child',             sortOrder: 3 },
  { key: 'CONCESSION',      label: 'Concession',        sortOrder: 4 },
  { key: 'ADULT',           label: 'Adult',             sortOrder: 5 },
  { key: 'PENSIONER',       label: 'Pensioner',         sortOrder: 6 },
] as const;

/** The set of valid standard tier keys. */
export const STANDARD_TICKET_TIER_KEYS: readonly string[] =
  STANDARD_TICKET_TIERS.map((t) => t.key);

/** Token in a tier label replaced with the school's own name. */
export const SCHOOL_NAME_TOKEN = '{SCHOOL}';

/**
 * Resolve a tier's display label, substituting the school name into the
 * {SCHOOL} token. Falls back to the raw label for an unknown key.
 */
export function resolveTierLabel(key: string, schoolName: string): string {
  const def = STANDARD_TICKET_TIERS.find((t) => t.key === key);
  if (!def) return key;
  return def.label.replaceAll(SCHOOL_NAME_TOKEN, schoolName);
}

/** Look up a standard tier definition by key. */
export function getStandardTier(key: string): StandardTicketTier | undefined {
  return STANDARD_TICKET_TIERS.find((t) => t.key === key);
}
